import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { EntitySelect } from '@/components/EntitySelect';
import { MultiSelect } from '@/components/ui/multi-select';
import { COUNTRIES } from '@/lib/countries';
import type { ApiError } from '@/core/api';
import {
  createContactOption,
  resolveContactLabel,
  searchContacts,
} from '@/features/crm/contacts/select';
import { INDUSTRIES, MAX_TECHNOLOGIES, MAX_TECHNOLOGY_LENGTH } from '../constants';
import { useCompanyTechnologies } from '../queries';
import type { CompanyInput } from '../types';

// Radix Select can't carry an empty value — this stands in for "not set".
const NO_INDUSTRY = '__none__';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  website: z.string().url('Enter a valid URL').or(z.literal('')),
  address: z.string().max(255),
  country: z.string().max(2, 'Use the 2-letter ISO code'),
  industry: z.string(),
  technologies: z
    .array(z.string().max(MAX_TECHNOLOGY_LENGTH, `Keep each under ${MAX_TECHNOLOGY_LENGTH} characters`))
    .max(MAX_TECHNOLOGIES, `At most ${MAX_TECHNOLOGIES} technologies`),
  notes: z.string(),
  is_active: z.boolean(),
  // Contact id to link to this company ('' = none). Not part of the company
  // payload — the page links it after the company is saved.
  contact: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'name',
  'website',
  'address',
  'country',
  'industry',
  'technologies',
  'notes',
  'is_active',
] as const;

interface CompanyFormProps {
  defaultValues?: Partial<CompanyInput> & { contact?: string };
  submitLabel: string;
  /** Persists the company, then links `contactId` to it; may reject with {@link ApiError}. */
  onSubmit: (values: CompanyInput, contactId: string) => Promise<unknown>;
  onCancel: () => void;
}

export function CompanyForm({ defaultValues, submitLabel, onSubmit, onCancel }: CompanyFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      website: '',
      address: '',
      country: '',
      industry: '',
      technologies: [],
      notes: '',
      is_active: true,
      contact: '',
      ...defaultValues,
    },
  });
  const technologiesQuery = useCompanyTechnologies();
  const technologyOptions = (technologiesQuery.data ?? []).map((value) => ({ value }));

  async function handleSubmit(values: FormValues) {
    const { contact, ...company } = values;
    try {
      await onSubmit(
        {
          ...company,
          country: company.country.trim().toUpperCase(),
          industry: company.industry as CompanyInput['industry'],
        },
        contact,
      );
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        // Map backend 400/422 field errors onto the form (CLAUDE.md §2.6).
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
      // Non-field errors are surfaced by the mutation's toast.
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-6">
        <FormSection variant="card" title="Company" description="Who are they?">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Inc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input type="url" placeholder="https://acme.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Market St, San Francisco" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Profile"
          description="What the client is — shared by every lead and deal with them."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industry</FormLabel>
                  <Select
                    value={field.value || NO_INDUSTRY}
                    onValueChange={(v) => field.onChange(v === NO_INDUSTRY ? '' : v)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_INDUSTRY}>Not set</SelectItem>
                      {INDUSTRIES.map((i) => (
                        <SelectItem key={i.value} value={i.value}>
                          {i.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="technologies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Technologies</FormLabel>
                  <FormControl>
                    <MultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={technologyOptions}
                      creatable
                      placeholder="Their stack, e.g. Shopify"
                      searchPlaceholder="Search or add…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Status" description="Active in our pipeline?">
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Active</FormLabel>
                <FormControl>
                  <div className="border-input flex h-9 items-center justify-between rounded-md border bg-transparent px-3 shadow-xs dark:bg-input/30">
                    <span className="text-muted-foreground text-sm">
                      {field.value
                        ? 'Visible in default lists'
                        : 'Hidden from default lists'}
                    </span>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-label="Active"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          variant="card"
          title="Contact"
          description="Link a person at this company — pick one or create a new one."
        >
          <FormField
            control={form.control}
            name="contact"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="company-contact-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchContacts}
                    resolveLabel={resolveContactLabel}
                    onCreate={createContactOption}
                    placeholder="No contact"
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Notes" description="Free-form context.">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    rows={4}
                    placeholder="Anything worth remembering…"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
